import { VariableBenefitSourceJurisdiction } from '../../../../../models/submissions/variable-benefit-source-jurisdiction';
import { LockedUnlockedBalance } from '../../../../../models/shared/locked-unlocked-balance';
import { GoverningJurisdictionTypeId } from '../../../../../models/enum/shared/governing-jurisdiction-type-id';
import { vbSourceJurisdictionAmounts } from './vb-source-jurisdiction-amounts.utils';

export interface VbAssetsUsedCard {
  jurisdictionId: GoverningJurisdictionTypeId;
  name: string;
  jurisdictionTotal: number;
  hasUnlock: boolean;
  showUnusedUnlockAsterisk: boolean;
  showLockedSection: boolean;
  lockedBalance: number;
  effectiveLockedBalance: number;
  showLockedStrikethrough: boolean;
  useEntireLockedBalance: boolean;
  showUnlockedSection: boolean;
  unlockedBalance: number;
  effectiveUnlockedBalance: number;
  showUnlockedStrikethrough: boolean;
  useEntireUnlockedBalance: boolean;
}

export interface VbAssetsUsedSummary {
  cards: VbAssetsUsedCard[];
  total: number;
  showEntireBalanceDisclaimer: boolean;
  showUnlockedFundsDisclaimer: boolean;
  unlockedFundsMarker: string;
}

/** One card per DC source, so accounts treated as the same final location are never merged. */
export function vbAssetsUsedSummary(
  sources: VariableBenefitSourceJurisdiction[],
  balances: LockedUnlockedBalance[] | null
): VbAssetsUsedSummary {
  const cards = sources.map(source => toCard(source, balances)).sort((a, b) => a.name.localeCompare(b.name));
  const showEntireBalanceDisclaimer = cards.some(card => card.useEntireLockedBalance || card.useEntireUnlockedBalance);

  return {
    cards,
    total: cards.reduce((sum, card) => sum + card.jurisdictionTotal, 0),
    showEntireBalanceDisclaimer,
    showUnlockedFundsDisclaimer: cards.some(card => card.showUnusedUnlockAsterisk),
    unlockedFundsMarker: showEntireBalanceDisclaimer ? '**' : '*'
  };
}

function toCard(source: VariableBenefitSourceJurisdiction, balances: LockedUnlockedBalance[] | null): VbAssetsUsedCard {
  const balance = balances?.find(b => b.governingJurisdictionId === source.governingJurisdictionId) ?? null;
  const { hasUnlock, originalLocked, effectiveLocked, originalUnlocked, effectiveUnlocked } =
    vbSourceJurisdictionAmounts(source, balance);

  return {
    jurisdictionId: source.governingJurisdictionId,
    name: source.governingJurisdiction?.name ?? '',
    jurisdictionTotal: effectiveLocked + effectiveUnlocked,
    hasUnlock,
    showUnusedUnlockAsterisk: hasUnlock && !source.useEntireUnlockedBalance,
    showLockedSection: effectiveLocked > 0,
    lockedBalance: originalLocked,
    effectiveLockedBalance: effectiveLocked,
    showLockedStrikethrough: hasUnlock && differsWhenRoundedToCents(originalLocked, effectiveLocked),
    useEntireLockedBalance: source.useEntireLockedBalance,
    showUnlockedSection: effectiveUnlocked > 0,
    unlockedBalance: originalUnlocked,
    effectiveUnlockedBalance: effectiveUnlocked,
    showUnlockedStrikethrough: hasUnlock && differsWhenRoundedToCents(originalUnlocked, effectiveUnlocked),
    useEntireUnlockedBalance: source.useEntireUnlockedBalance
  };
}

// A percentage unlock can leave a sub-cent difference between the two amounts. Both render
// through the currency pipe (rounded to cents), so compare what the member actually sees —
// otherwise the strikethrough sits over a number identical to the one beside it.
function differsWhenRoundedToCents(a: number, b: number): boolean {
  return Math.round(a * 100) !== Math.round(b * 100);
}
